import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { isAllowedTimeStepsForInlinedCumulativeSums } from '@weenat/client/dist/core/cumulative-sums'
import { HistoryPeriod } from '@weenat/client/dist/core/history'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { useIntl } from '@weenat/wintl'
import DelimitedFlex from '../kit/DelimitedFlex'
import Grid from '../kit/Grid'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import Text from '../kit/Text'
import TextNumber from '../kit/TextNumber'
import PeriodBasedCumulativeSum from './PeriodBasedCumulativeSum'

interface AggregateBlockProps {
  label: string
  value: number | null
}

const AggregateBlock: FC<AggregateBlockProps> = ({ label, value }) => {
  return (
    <Flex
      $p={'md'}
      $gap={'md'}
      $justifyContent='space-between'
      $borderRadius={'md'}
      $backgroundColor={'grayscale.100'}
      $width={'100%'}
      $flex={1}
    >
      <Text>{label}</Text>
      <TextNumber $fontWeight='bold' $textAlign='right'>
        {value}
      </TextNumber>
    </Flex>
  )
}

interface PeriodBasedMetricDigestProps {
  metric: DeviceAvailableMeasure
  data: DataPoint[]
  isLoading: boolean
  period: HistoryPeriod
  timeStep: TimeSteps
  deviceId: number
}

const PeriodBasedMetricDigest: FC<PeriodBasedMetricDigestProps> = ({
  metric,
  data,
  isLoading,
  period,
  timeStep,
  deviceId
}) => {
  const { t } = useIntl()
  const { convertValue } = useConvertedValue()

  if (isLoading) {
    return <LoadingCircle />
  }

  const values = data
    .map((d) => d.value)
    .filter((v): v is number => v !== null && Number.isFinite(v))

  let avg: number | null = null
  let min: number | null = null
  let max: number | null = null

  switch (metric) {
    case 'PPFD':
    case 'SSI':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0

      return (
        <>
          <DelimitedFlex $isDelimitedOnTop $p={'md'} $gap={'md'} $height={'auto'}>
            {/* Average */}
            <AggregateBlock
              label={t('charts.average', { capitalize: true })}
              value={convertValue({ metric, value: avg, displayUnit: true })}
            />
            {/* Maximum */}
            <AggregateBlock
              label={t('charts.max', { capitalize: true })}
              value={convertValue({ metric, value: max, displayUnit: true })}
            />
            {/* Minimum */}
            <AggregateBlock
              label={t('charts.min', { capitalize: true })}
              value={convertValue({ metric, value: min, displayUnit: true })}
            />
          </DelimitedFlex>
          {/* Cumulative Sums */}
          {isAllowedTimeStepsForInlinedCumulativeSums(timeStep) ? (
            <Grid $templateRows='1fr' $templateColumns='1fr 1fr' $columnGap={'md'} $p={'md'}>
              <PeriodBasedCumulativeSum
                metric={'SSI_HOURSAccumulation'}
                period={period}
                timeStep={timeStep}
                deviceId={deviceId}
              />
            </Grid>
          ) : null}
        </>
      )
    case 'U':
    case 'T_SOIL':
    case 'T_WET':
    case 'T_DRY':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0

      return (
        <DelimitedFlex $isDelimitedOnTop $p={'md'} $gap={'md'} $height={'auto'}>
          {/* Average */}
          <AggregateBlock
            label={t('charts.average', { capitalize: true })}
            value={convertValue({ metric, value: avg, displayUnit: true })}
          />

          {/* Maximum */}
          <AggregateBlock
            label={t('charts.max', { capitalize: true })}
            value={convertValue({ metric, value: max, displayUnit: true })}
          />

          {/* Minimum */}
          <AggregateBlock
            label={t('charts.min', { capitalize: true })}
            value={convertValue({ metric, value: min, displayUnit: true })}
          />
        </DelimitedFlex>
      )

    case 'T':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0
      const amp = Math.abs(max - min)

      return (
        <>
          <DelimitedFlex $isDelimitedOnTop $p={'md'} $gap={'md'} $height={'auto'}>
            {/* Average */}
            <AggregateBlock
              label={t('charts.average', { capitalize: true })}
              value={convertValue({ metric, value: avg, displayUnit: true })}
            />

            {/* Maximum */}
            <AggregateBlock
              label={t('charts.max', { capitalize: true })}
              value={convertValue({ metric, value: max, displayUnit: true })}
            />

            {/* Minimum */}
            <AggregateBlock
              label={t('charts.min', { capitalize: true })}
              value={convertValue({ metric, value: min, displayUnit: true })}
            />

            {/* Amplitude */}
            <AggregateBlock
              label={t('charts.spread', { capitalize: true })}
              value={convertValue({ metric, value: amp, displayUnit: true })}
            />
          </DelimitedFlex>

          {/* Cumulative Sums */}
          {isAllowedTimeStepsForInlinedCumulativeSums(timeStep) ? (
            <Grid $templateRows='1fr' $templateColumns='1fr 1fr' $columnGap={'md'} $p={'md'}>
              {timeStep === TimeSteps.perDays ? (
                <PeriodBasedCumulativeSum
                  metric={'heatAccumulation'}
                  period={period}
                  timeStep={timeStep}
                  deviceId={deviceId}
                />
              ) : null}
              <PeriodBasedCumulativeSum
                metric={'COLD_T_HOUR'}
                period={period}
                timeStep={timeStep}
                deviceId={deviceId}
              />
            </Grid>
          ) : null}
        </>
      )

    case 'RR':
      {
        /* Total sum of rain */
      }
      const sum = data.reduce((acc, d) => acc + (d.value ?? 0), 0)
      return (
        <DelimitedFlex $isDelimitedOnTop $p={'md'} $gap={'md'}>
          <AggregateBlock
            label={t('charts.cumulation', { capitalize: true })}
            value={convertValue({ metric, value: sum, displayUnit: true })}
          />
        </DelimitedFlex>
      )

    // Not declaring LW_D on purpose to leave null content
    case 'LW_V':
      return isAllowedTimeStepsForInlinedCumulativeSums(timeStep) ? (
        <DelimitedFlex $isDelimitedOnTop $p={'md'} $gap={'md'}>
          <PeriodBasedCumulativeSum
            metric={'leafWetnessAccumulation'}
            period={period}
            timeStep={timeStep}
            deviceId={deviceId}
          />
        </DelimitedFlex>
      ) : null

    default:
      return null
  }
}

export default PeriodBasedMetricDigest
